/* FND Predictive Search — Nike-style full-width overlay */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input        = this.querySelector('input[type="search"]');
    this.form         = this.querySelector('form');
    this._panel       = null;
    this._backdrop    = null;
    this._abort       = null;
    this._timer       = null;
    this._query       = '';
    this._open        = false;

    if (!this.input) return;
    this._build();
    this._bind();
  }

  /* ── DOM setup — singleton: panel condiviso tra tutte le istanze ── */
  _build() {
    let backdrop = document.getElementById('fnd-ps-backdrop');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.id = 'fnd-ps-backdrop';
      backdrop.hidden = true;
      document.body.appendChild(backdrop);
    }
    this._backdrop = backdrop;

    let panel = document.getElementById('fnd-ps-panel');
    if (!panel) {
      panel = document.createElement('div');
      panel.id = 'fnd-ps-panel';
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-label', 'Risultati ricerca');
      panel.hidden = true;
      document.body.appendChild(panel);
    }
    this._panel = panel;
  }

  _bind() {
    this.input.addEventListener('input',   () => this._onInput());
    this.input.addEventListener('focus',   () => { if (this._query.length >= 2) this._show(); });
    this.input.addEventListener('keydown', (e) => this._onKey(e));

    this._backdrop.addEventListener('click', () => this._hide());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this._hide(); });

    /* close if another <details> opens (nav menu etc.) */
    document.addEventListener('toggle', () => this._hide(), true);
  }

  /* ── Position panel ── */
  _reposition() {
    if (window.innerWidth < 990) {
      /* Mobile: posiziona sotto l'input di ricerca */
      const rect = this.input.getBoundingClientRect();
      const top  = Math.round(rect.bottom) + 8;
      this._panel.style.top       = top + 'px';
      this._panel.style.maxHeight = (window.innerHeight - top - 8) + 'px';
    } else {
      const hdr = document.querySelector('.header-wrapper') || document.querySelector('sticky-header') || document.querySelector('header');
      const top = hdr ? hdr.getBoundingClientRect().bottom : 64;
      this._panel.style.top    = top + 'px';
      this._backdrop.style.top = top + 'px';
    }
  }

  /* ── Input handler ── */
  _onInput() {
    const q = this.input.value.trim();
    this._query = q;
    clearTimeout(this._timer);

    if (q.length < 2) { this._hide(); return; }

    this.setAttribute('loading', '');
    this._timer = setTimeout(() => this._fetch(q), 280);
  }

  _onKey(e) {
    if (!this._open) return;
    const items = [...this._panel.querySelectorAll('.fnd-ps-card, .fnd-ps-qrow')];
    const cur   = this._panel.querySelector('[aria-selected="true"]');
    let   idx   = items.indexOf(cur);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      idx = (idx + 1) % items.length;
      this._setActive(items, idx);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      idx = (idx - 1 + items.length) % items.length;
      this._setActive(items, idx);
    } else if (e.key === 'Enter' && cur) {
      const link = cur.tagName === 'A' ? cur : cur.querySelector('a');
      if (link) { e.preventDefault(); window.location.href = link.href; }
    }
  }

  _setActive(items, idx) {
    items.forEach((el, i) => el.setAttribute('aria-selected', i === idx ? 'true' : 'false'));
  }

  /* ── Fetch ── */
  async _fetch(q) {
    if (this._abort) this._abort.abort();
    this._abort = new AbortController();
    try {
      const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,query&resources[limit]=4&resources[options][unavailable_products]=last`;
      const res  = await fetch(url, { signal: this._abort.signal });
      if (!res.ok) throw new Error();
      const data = await res.json();
      this.removeAttribute('loading');
      const results  = data.resources?.results || {};
      this._render(q, results.products || [], results.queries || []);
    } catch(err) {
      if (err.name !== 'AbortError') { this.removeAttribute('loading'); }
    }
  }

  /* ── Render: sceglie layout in base a viewport ── */
  _render(q, products, queries) {
    if (window.innerWidth < 990) {
      this._renderMobile(q, products, queries);
    } else {
      this._renderDesktop(q, products, queries);
    }
    this._show();
  }

  /* ── Layout Desktop Nike (2 colonne) ── */
  _renderDesktop(q, products, queries) {
    const searchUrl = `/search?q=${encodeURIComponent(q)}&type=product`;
    const queryRows = queries.length
      ? queries.map(s => `
          <a href="${s.url}" class="fnd-ps-qrow" role="option" aria-selected="false">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <circle cx="6" cy="6" r="4.2" stroke="currentColor" stroke-width="1.4"/>
              <line x1="9.2" y1="9.2" x2="13" y2="13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
            </svg>
            <span>${this._highlight(s.styled_text || s.text, q)}</span>
          </a>`).join('')
      : `<a href="${searchUrl}" class="fnd-ps-qrow" role="option" aria-selected="false">
           <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
             <circle cx="6" cy="6" r="4.2" stroke="currentColor" stroke-width="1.4"/>
             <line x1="9.2" y1="9.2" x2="13" y2="13" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
           </svg>
           <span>${this._highlight(q, q)}</span>
         </a>`;

    let productHTML = products.length === 0
      ? `<p class="fnd-ps-noresults">Nessun prodotto trovato.</p>`
      : `<div class="fnd-ps-grid">${products.map(p => {
          const img     = p.featured_image?.url ? `<img src="${p.featured_image.url}&width=400" alt="${this._esc(p.featured_image.alt || p.title)}" width="400" height="400" loading="lazy">` : `<div class="fnd-ps-card__placeholder">✦</div>`;
          const price   = this._money(p.price);
          const compare = p.compare_at_price > p.price ? `<s class="fnd-ps-card__compare">${this._money(p.compare_at_price)}</s>` : '';
          const sold    = !p.available ? `<span class="fnd-ps-card__sold">Esaurito</span>` : '';
          return `<a href="${p.url}" class="fnd-ps-card" role="option" aria-selected="false">
            <div class="fnd-ps-card__img">${img}</div>
            <div class="fnd-ps-card__body">
              <p class="fnd-ps-card__name">${this._highlight(p.title, q)}</p>
              <p class="fnd-ps-card__price">${price}${compare}${sold}</p>
            </div></a>`;
        }).join('')}</div>`;

    this._panel.innerHTML = `
      <div class="fnd-ps-inner">
        <div class="fnd-ps-col-left">
          <p class="fnd-ps-col-label">Suggerimenti</p>
          <div class="fnd-ps-queries" role="listbox">${queryRows}</div>
          <a href="${searchUrl}" class="fnd-ps-seeall">Tutti i risultati
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </a>
        </div>
        <div class="fnd-ps-col-right">
          <p class="fnd-ps-col-label">Prodotti</p>
          ${productHTML}
        </div>
      </div>`;
  }

  /* ── Layout Mobile Nike (suggerimenti grandi + griglia 2 col) ── */
  _renderMobile(q, products, queries) {
    const searchUrl = `/search?q=${encodeURIComponent(q)}&type=product`;
    let html = '';

    if (queries.length) {
      html += `<p class="fnd-ps-mob-section-label">Suggerimenti principali</p><ul class="fnd-ps-mob-queries">`;
      queries.forEach(s => {
        const text  = this._esc(s.text || '');
        const re    = new RegExp(`^(${q.replace(/[.*+?^${}()|[\]\\]/g,'\\$&')})(.*)`, 'i');
        const match = text.match(re);
        const label = match ? `<strong>${match[1]}</strong>${match[2]}` : `<strong>${text}</strong>`;
        html += `<li><a href="${this._esc(s.url)}" class="fnd-ps-mob-qrow">${label}</a></li>`;
      });
      html += `</ul>`;
    }

    if (products.length === 0) {
      html += `<p class="fnd-ps-mob-empty">Nessun prodotto trovato per <strong>${this._esc(q)}</strong></p>`;
    } else {
      const cards = products.map(p => {
        const img     = p.featured_image?.url ? `<img src="${p.featured_image.url}&width=400" alt="${this._esc(p.featured_image.alt || p.title)}" width="400" height="400" loading="lazy">` : `<div class="fnd-ps-mob-card-noimg">✦</div>`;
        const price   = this._money(p.price);
        const compare = p.compare_at_price > p.price ? `<s class="fnd-ps-mob-card-compare">${this._money(p.compare_at_price)}</s>` : '';
        return `<a href="${p.url}" class="fnd-ps-mob-card">
          <div class="fnd-ps-mob-card-img">${img}</div>
          <div class="fnd-ps-mob-card-body">
            <p class="fnd-ps-mob-card-name">${this._esc(p.title)}</p>
            <p class="fnd-ps-mob-card-price">${price}${compare}</p>
          </div></a>`;
      }).join('');
      html += `<div class="fnd-ps-mob-grid">${cards}</div>`;
    }

    html += `<a href="${searchUrl}" class="fnd-ps-mob-all">Vedi tutti i risultati per &ldquo;<strong>${this._esc(q)}</strong>&rdquo;
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></a>`;
    this._panel.innerHTML = `<div class="fnd-ps-mob-inner">${html}</div>`;
  }

  /* ── Utils ── */
  _money(cents) {
    return new Intl.NumberFormat('it-IT', { style:'currency', currency:'EUR' }).format(cents / 100);
  }
  _esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }
  _highlight(text, q) {
    const safe  = this._esc(text);
    const safeQ = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return safe.replace(new RegExp(`(${safeQ})`, 'gi'), '<mark>$1</mark>');
  }

  /* ── Show / Hide ── */
  _show() {
    this._reposition();
    this._panel.hidden    = false;
    this._backdrop.hidden = false;
    this.setAttribute('open', '');
    this._open = true;
    document.body.classList.add('fnd-ps-active');
  }
  _hide() {
    this._panel.hidden    = true;
    this._backdrop.hidden = true;
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this._open = false;
    document.body.classList.remove('fnd-ps-active');
  }

  get isOpen() { return this._open; }
}

/* ── Styles ── */
const S = document.createElement('style');
S.textContent = `
/* Backdrop */
#fnd-ps-backdrop {
  display: none;
  position: fixed;
  left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,.55);
  z-index: 8998;
}
#fnd-ps-backdrop:not([hidden]) { display: block; }

/* Panel */
#fnd-ps-panel {
  position: fixed;
  left: 0; right: 0; bottom: 0;
  background: #0a0a0a;
  border-top: 1px solid #1c1c1c;
  z-index: 8999;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  font-family: 'Figtree', sans-serif;
  max-height: calc(100vh - 80px);
}

/* Inner layout */
.fnd-ps-inner {
  display: grid;
  grid-template-columns: 220px 1fr;
  max-width: 1280px;
  margin: 0 auto;
  padding: 32px 40px 40px;
  gap: 48px;
}

/* Column label */
.fnd-ps-col-label {
  font-size: 9px;
  letter-spacing: .2em;
  text-transform: uppercase;
  color: rgba(255,255,255,.3);
  margin: 0 0 16px;
}

/* Query suggestions */
.fnd-ps-queries { display: flex; flex-direction: column; gap: 2px; }
.fnd-ps-qrow {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 8px;
  border-radius: 2px;
  text-decoration: none;
  color: rgba(255,255,255,.75);
  font-size: 13px;
  line-height: 1.3;
  transition: background .12s, color .12s;
  cursor: pointer;
}
.fnd-ps-qrow svg { flex-shrink: 0; color: rgba(255,255,255,.3); }
.fnd-ps-qrow:hover,
.fnd-ps-qrow[aria-selected="true"] { background: #161616; color: #fff; }
.fnd-ps-qrow mark { background: transparent; color: #fff; font-weight: 700; }

.fnd-ps-seeall {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: #42AFEB;
  text-decoration: none;
}
.fnd-ps-seeall:hover { text-decoration: underline; }

/* Product grid */
.fnd-ps-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}
@media (max-width: 1100px) { .fnd-ps-grid { grid-template-columns: repeat(3, 1fr); } }

.fnd-ps-card {
  display: block;
  text-decoration: none;
  color: #fff;
}
.fnd-ps-card[aria-selected="true"] .fnd-ps-card__img,
.fnd-ps-card:hover .fnd-ps-card__img { outline: 2px solid #42AFEB; }

.fnd-ps-card__img {
  aspect-ratio: 1/1;
  background: #111;
  overflow: hidden;
  border-radius: 2px;
  margin-bottom: 10px;
}
.fnd-ps-card__img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform .3s ease;
}
.fnd-ps-card:hover .fnd-ps-card__img img { transform: scale(1.04); }
.fnd-ps-card__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,.1);
  font-size: 22px;
}

.fnd-ps-card__body { padding: 0 2px; }
.fnd-ps-card__name {
  font-family: 'Oswald', sans-serif;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: .05em;
  text-transform: uppercase;
  color: #fff;
  line-height: 1.3;
  margin: 0 0 7px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.fnd-ps-card__name mark { background: transparent; color: #42AFEB; }
.fnd-ps-card__price {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255,255,255,.7);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
}
.fnd-ps-card__compare {
  font-weight: 400;
  font-size: 12px;
  color: rgba(255,255,255,.35);
}
.fnd-ps-card__sold {
  font-size: 9px;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: rgba(255,255,255,.3);
}

.fnd-ps-noresults {
  color: rgba(255,255,255,.4);
  font-size: 14px;
  padding: 8px 0;
}

/* ── Mobile layout nel pannello ── */
@media (max-width: 989px) {
  #fnd-ps-backdrop { display: none !important; }

  #fnd-ps-panel {
    z-index: 99999;
    border-radius: 0 0 12px 12px;
    border-top: 1px solid #1c1c1c;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .fnd-ps-mob-inner {
    padding: 20px 20px 32px;
  }
}

/* Mobile query suggestions */
.fnd-ps-mob-section-label {
  font-family: 'Figtree', sans-serif;
  font-size: 12px;
  color: rgba(255,255,255,.4);
  margin: 0 0 14px;
  font-weight: 400;
}
.fnd-ps-mob-queries {
  list-style: none;
  padding: 0;
  margin: 0 0 28px;
}
.fnd-ps-mob-qrow {
  display: block;
  font-family: 'Figtree', sans-serif;
  font-size: 20px;
  font-weight: 400;
  color: rgba(255,255,255,.6);
  text-decoration: none;
  padding: 9px 0;
  line-height: 1.2;
}
.fnd-ps-mob-qrow strong { color: #fff; font-weight: 700; }
.fnd-ps-mob-qrow:active { color: #42AFEB; }

/* Mobile product grid */
.fnd-ps-mob-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}
.fnd-ps-mob-card { display: block; text-decoration: none; color: #fff; }
.fnd-ps-mob-card-img {
  aspect-ratio: 1 / 1;
  background: #111;
  overflow: hidden;
  border-radius: 2px;
  margin-bottom: 10px;
}
.fnd-ps-mob-card-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.fnd-ps-mob-card-noimg {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  color: rgba(255,255,255,.1); font-size: 24px;
}
.fnd-ps-mob-card-name {
  font-family: 'Oswald', sans-serif;
  font-size: 13px; font-weight: 500;
  letter-spacing: .04em; text-transform: uppercase;
  color: #fff; margin: 0 0 5px; line-height: 1.3;
  display: -webkit-box; -webkit-line-clamp: 2;
  -webkit-box-orient: vertical; overflow: hidden;
}
.fnd-ps-mob-card-price {
  font-size: 13px; font-weight: 700; color: #fff;
  display: flex; align-items: center; gap: 5px;
}
.fnd-ps-mob-card-compare { font-weight: 400; font-size: 11px; color: rgba(255,255,255,.35); }

.fnd-ps-mob-all {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 0; font-size: 11px; font-weight: 700;
  letter-spacing: .1em; text-transform: uppercase;
  color: #42AFEB; text-decoration: none;
  border-top: 1px solid #1a1a1a;
}
.fnd-ps-mob-all strong { color: #fff; font-weight: 600; text-transform: none; letter-spacing: 0; }
.fnd-ps-mob-empty { padding: 20px 0; font-size: 14px; color: rgba(255,255,255,.45); }
.fnd-ps-mob-empty strong { color: #fff; }
`;
document.head.appendChild(S);

customElements.define('predictive-search', PredictiveSearch);
